import React, { useState } from 'react';
import apiService from '../../services/api';

type StatusMessage = {
  type: 'success' | 'error';
  message: string;
};

const ImportServices: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setStatusMessage(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      const blob = await apiService.downloadProvidersTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'service_providers_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setStatusMessage({
        type: 'success',
        message: 'تم تنزيل ملف القالب بنجاح. يرجى اتباع الأعمدة الموجودة فيه.',
      });
    } catch (error: any) {
      console.error('Error downloading template:', error);
      setStatusMessage({
        type: 'error',
        message: error.message || 'تعذّر تنزيل ملف القالب، حاول مرة أخرى.',
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      setIsDownloadingSample(true);
      const blob = await apiService.exportProviders();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `service_providers_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setStatusMessage({
        type: 'success',
        message: 'تم تنزيل ملف البيانات الحالي بنجاح.',
      });
    } catch (error: any) {
      console.error('Error downloading sample data:', error);
      setStatusMessage({
        type: 'error',
        message: error.message || 'تعذّر تنزيل ملف البيانات الحالي.',
      });
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const handleImport = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedFile) {
      setStatusMessage({
        type: 'error',
        message: 'يرجى اختيار ملف إكسل أولاً قبل البدء في الاستيراد.',
      });
      return;
    }

    try {
      setIsUploading(true);
      setStatusMessage(null);
      await apiService.importProviders(selectedFile);
      setStatusMessage({
        type: 'success',
        message: 'تم استيراد الخدمات ومزودي الخدمات بنجاح.',
      });
      setSelectedFile(null);
    } catch (error: any) {
      console.error('Error importing services:', error);
      setStatusMessage({
        type: 'error',
        message: error.message || 'تعذّر إجراء عملية الاستيراد. تأكد من صحة البيانات في الملف.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">استيراد الخدمات عبر ملف إكسل</h1>
        <p className="text-gray-600">
          قم باستخدام القالب المرفق لتجهيز بيانات مزودي الخدمة والخدمات المرتبطة بهم. سيتم إنشاء حساب جديد لكل مزود خدمة غير موجود،
          وإضافة الخدمة تلقائياً مع محاولة تحديد الإحداثيات عبر عنوان الخدمة ما لم تقم بتحديد الإحداثيات يدوياً في الملف. كلمة المرور الافتراضية للحسابات الجديدة هي <code className="bg-gray-100 px-1 rounded">password123</code>.
        </p>
      </div>

      {statusMessage && (
        <div
          className={`border-l-4 p-4 rounded ${
            statusMessage.type === 'success'
              ? 'bg-green-50 border-green-400 text-green-700'
              : 'bg-red-50 border-red-400 text-red-700'
          }`}
        >
          {statusMessage.message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">خطوات الاستيراد</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
            <li>قم بتنزيل ملف القالب الفارغ أو ملف البيانات الحالي للاطلاع على الأعمدة المطلوبة.</li>
            <li>
              عبّئ الأعمدة التالية بدقة:
              <ul className="list-disc list-inside mr-6 mt-2 text-sm text-gray-600 space-y-1">
                <li><strong>email</strong>: البريد الإلكتروني الفريد لمزود الخدمة.</li>
                <li><strong>first_name / last_name</strong> و<strong>first_name_ar / last_name_ar</strong>: الاسم باللغتين.</li>
                <li><strong>phone</strong>: رقم التواصل الدولي.</li>
                <li><strong>address</strong>: عنوان الخدمة الكامل (يُستخدم لتحديد الإحداثيات إذا لم تُحدّد إحداثيات يدوية).</li>
                <li><strong>category_name</strong>: اسم الفئة بنفس اللغة المختارة حالياً في لوحة التحكم. يمكن بدلاً من ذلك استخدام العمود <strong>category_id</strong> إذا كنت تعرف الرقم بشكل دقيق.</li>
                <li><strong>city_name</strong>: اسم المدينة المطابق لقاعدة البيانات. يمكن بدلاً من ذلك استخدام العمود <strong>city_id</strong> إذا كنت تعرف الرقم بشكل دقيق.</li>
                <li><strong>latitude</strong> و<strong>longitude</strong> (اختياري): في حال توفرت الإحداثيات، سيتم استخدامها مباشرة بدلاً من محاولة الاستدلال عليها من العنوان.</li>
                <li><strong>service_name</strong> و<strong>website</strong> (اختياري).</li>
              </ul>
            </li>
            <li>احفظ الملف بصيغة <code className="bg-gray-100 px-1 rounded">.xlsx</code> أو <code className="bg-gray-100 px-1 rounded">.xls</code>.</li>
            <li>قم برفع الملف إلى النموذج أدناه ثم اضغط على زر الاستيراد.</li>
          </ol>
        </div>

        <div className="bg-white shadow rounded-lg p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">التحميلات</h3>
            <div className="space-y-2">
              <button
                type="button"
                onClick={handleDownloadTemplate}
                disabled={isDownloadingTemplate}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
              >
                {isDownloadingTemplate ? 'جاري التحميل...' : 'تحميل قالب الاستيراد'}
              </button>
              <button
                type="button"
                onClick={handleDownloadSample}
                disabled={isDownloadingSample}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-60"
              >
                {isDownloadingSample ? 'جاري التحميل...' : 'تحميل ملف بيانات حالي'}
              </button>
            </div>
          </div>

          <form onSubmit={handleImport} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ملف الاستيراد (Excel)</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-900 bg-gray-50 border border-gray-300 rounded-lg cursor-pointer focus:outline-none"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-blue-600">
                  الملف المحدد: <span className="font-medium">{selectedFile.name}</span>
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                الحد الأقصى الموصى به 2,000 صف لكل عملية استيراد لتفادي طول وقت التنفيذ.
              </p>
            </div>

            <button
              type="submit"
              disabled={isUploading || !selectedFile}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-60"
            >
              {isUploading ? 'جاري الاستيراد...' : 'استيراد الخدمات'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImportServices;


